'use client';

import React, { createContext, useContext, useMemo, useState } from 'react';
import type { ITimeZone } from '@dealroadshow/uikit-date-time';
import { DEFAULT_TIME_ZONE } from '@framework/timezone/constants';
import { resolveTimeZone } from '@/creditflowresearch/ui/components/ActiveCalendar/helpers/dates';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useDictionariesContext as useDictionariesContextV2 } from '@/creditflowresearch/application/Dictionaries/v2/DictionariesContext';
import useDetectUserTimeZone from '@/creditflowresearch/application/User/UserTimeZone/hooks/useDetectUserTimeZone';
import useTimeZoneDetectionAlert from '@/creditflowresearch/application/User/UserTimeZone/hooks/useTimeZoneDetectionAlert';
import { ITimeZoneInfo } from '@/creditflowresearch/application/User/UserTimeZone/types';

export interface IUserTimeZoneContext {
  /** Resolved zone used for rendering; falls back to DEFAULT_TIME_ZONE. */
  timeZone: ITimeZone,
  /** Raw saved IANA name, or null when the user has no saved preference. */
  savedTimeZone: string | null,
  setTimeZone: (timeZone: string | null) => void,
  /** True once this page load auto-saved the browser's zone for a user who had none. */
  isTimeZoneJustDetected: boolean,
  isAutoDetectEnabled: boolean,
  setAutoDetect: (isEnabled: boolean) => void,
}

export const UserTimeZoneContext = createContext<IUserTimeZoneContext | null>(null);

export const useUserTimeZoneContext = (): IUserTimeZoneContext => {
  const context = useContext(UserTimeZoneContext);
  if (!context) {
    throw new Error('useUserTimeZoneContext must be used within the UserTimeZoneContext');
  }
  return context;
};

interface IProps {
  timeZoneInfo?: ITimeZoneInfo,
  children: React.ReactNode,
}

const UserTimeZoneContextProvider = ({
  children,
  timeZoneInfo,
}: IProps) => {
  const {
    userTimeZone,
    shouldAutoUpdateTimeZone = false,
    userAutoDetectTimeZone = true,
  } = timeZoneInfo ?? {};
  const legacy = useContext(DictionariesContext);
  const { timeZones: v2TimeZones } = useDictionariesContextV2();
  const timeZones = (legacy?.timeZones ?? v2TimeZones) as NonNullable<typeof legacy>['timeZones'];
  const [timeZoneIana, setTimeZone] = useState<string | null>(userTimeZone ?? null);
  const [isAutoDetectEnabled, setAutoDetect] = useState<boolean>(userAutoDetectTimeZone);

  // detect timezone for the first time, when isTimeZoneConfigured is false
  const isTimeZoneJustDetected = useDetectUserTimeZone({
    shouldAutoUpdateTimeZone,
    getByTimeZone: timeZones.getByTimeZone,
    onDetected: setTimeZone,
  });

  useTimeZoneDetectionAlert({
    savedTimeZone: timeZoneIana,
    isAutoDetectEnabled,
    shouldAutoUpdateTimeZone,
    setTimeZone,
    getByTimeZone: timeZones.getByTimeZone,
  });

  const timeZone = useMemo(
    () => (timeZoneIana ? resolveTimeZone(timeZones.getByTimeZone, timeZoneIana) : DEFAULT_TIME_ZONE),
    [timeZoneIana, timeZones],
  );

  const value = useMemo(
    () => ({
      timeZone,
      savedTimeZone: timeZoneIana,
      setTimeZone,
      isTimeZoneJustDetected,
      isAutoDetectEnabled,
      setAutoDetect,
    }),
    [timeZone, timeZoneIana, isTimeZoneJustDetected, isAutoDetectEnabled],
  );

  return (
    <UserTimeZoneContext.Provider value={ value }>
      { children }
    </UserTimeZoneContext.Provider>
  );
};

export default UserTimeZoneContextProvider;
